"use client";

/**
 * Auth gate for the Procore side panel — the Procore analogue of
 * `AddinAuthGate`. Two sequential steps, because Procore identity and
 * Procore data are different providers:
 *
 *   1. **Sign in to Magnus** — Magnus identity (Azure AD) is established in a
 *      first-party popup (`/procore/auth/start`) that mints a raw bearer
 *      session and hands it back via `notifySuccess({ sessionToken })`. We
 *      stash it (the generic `host-session-store`) and inject it on every
 *      request via `getEmbeddedSessionHeaders`.
 *   2. **Connect Procore** — Procore data access uses the standard per-user
 *      connector OAuth, launched in a popup at the generic connect route with
 *      a redirect back to `/procore/connected`.
 *
 * Once both are satisfied, the panel renders the normal app (chat). Popups
 * are opened by `@procore/procore-iframe-helpers`, which requires a user
 * gesture — every `authenticate()` call sits behind a button click.
 */
import { c as _c } from "react/compiler-runtime";
import { TRPCClientError } from "@trpc/client";
import { PROCORE_SURFACE } from "@workspace/copilots";
import { usePathname } from "next/navigation";
import { type ReactNode, useCallback, useEffect } from "react";
import { EmbeddedGateScreen } from "@/components/embedded/gate-screen";
import { clearHostSessionToken, setHostSessionToken } from "@/lib/host-session-store";
import { trpc } from "@/trpc/client";
type AuthStep = "signin" | "connect" | "ready";
async function openProcorePopup(config: {
  url: string;
  onSuccess: (payload: unknown) => void;
  onFailure: (error: unknown) => void;
}): Promise<void> {
  // Dynamic import: the helper touches `window`, so keep it out of the
  // server-rendered module graph for this client component.
  const {
    initialize
  } = await import("@procore/procore-iframe-helpers");
  const context = initialize();
  context.authentication.authenticate(config);
}
export function ProcoreAuthGate(t0) {
  const $ = _c(19);
  const {
    children
  } = t0;
  const pathname = usePathname();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      retry: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const connectorsQuery = trpc.connectors.list.useQuery(undefined, t1);
  const hasMagnusSession = connectorsQuery.isSuccess;
  const magnusChecked = !connectorsQuery.isPending;
  let t2;
  let t3;
  if ($[1] !== connectorsQuery.error) {
    t2 = () => {
      const error = connectorsQuery.error;
      if (error instanceof TRPCClientError && error.data?.code === "UNAUTHORIZED") {
        clearHostSessionToken(PROCORE_SURFACE);
      }
    };
    t3 = [connectorsQuery.error];
    $[1] = connectorsQuery.error;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  const procore = connectorsQuery.data?.find(_temp);
  const procoreConnected = procore?.isConnected ?? false;
  const procoreComingSoon = procore?.isComingSoon ?? false;
  let t4;
  if ($[4] !== connectorsQuery) {
    t4 = () => {
      openProcorePopup({
        url: "/procore/auth/start",
        onSuccess: payload => {
          const token = (payload as {
            sessionToken?: string;
          })?.sessionToken;
          if (token) {
            setHostSessionToken(PROCORE_SURFACE, token);
            connectorsQuery.refetch();
          }
        },
        onFailure: _temp2
      });
    };
    $[4] = connectorsQuery;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleSignIn = t4;
  let t5;
  if ($[6] !== connectorsQuery) {
    t5 = () => {
      openProcorePopup({
        url: "/api/connectors/procore/connect?redirect=/procore/connected",
        onSuccess: () => {
          connectorsQuery.refetch();
        },
        onFailure: _temp3
      });
    };
    $[6] = connectorsQuery;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handleConnect = t5;
  if (pathname?.startsWith("/procore")) {
    let t6;
    if ($[8] !== children) {
      t6 = <>{children}</>;
      $[8] = children;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    return t6;
  }
  let step = "signin";
  if (hasMagnusSession) {
    step = procoreConnected ? "ready" : "connect";
  }
  if (!magnusChecked) {
    let t6;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <EmbeddedGateScreen message={"Loading Magnus\u2026"} spinner={true} />;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    return t6;
  }
  if (step === "ready") {
    let t6;
    if ($[11] !== children) {
      t6 = <>{children}</>;
      $[11] = children;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    return t6;
  }
  if (step === "connect" && connectorsQuery.isLoading) {
    let t6;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <EmbeddedGateScreen message={"Checking your Procore connection\u2026"} spinner={true} />;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    return t6;
  }
  if (step === "connect" && procoreComingSoon) {
    let t6;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <EmbeddedGateScreen message="Magnus for Procore isn't enabled for your account yet. Contact your administrator to request access." title="Procore access not enabled" />;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    return t6;
  }
  if (step === "connect") {
    let t6;
    if ($[15] !== handleConnect) {
      t6 = <EmbeddedGateScreen action={{
        label: "Connect Procore",
        onClick: handleConnect
      }} message="Connect your Procore account to use Magnus in the side panel." title="Connect Procore" />;
      $[15] = handleConnect;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    return t6;
  }
  let t6;
  if ($[17] !== handleSignIn) {
    t6 = <EmbeddedGateScreen action={{
      label: "Sign in to Magnus",
      onClick: handleSignIn
    }} message="Sign in with your Magnus account to use the assistant inside Procore." title="Sign in to Magnus" />;
    $[17] = handleSignIn;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}
function _temp3() {}
function _temp2() {}
function _temp(c) {
  return c.id === "procore";
}